import { queryClient } from 'App';
import debounce from 'lodash/debounce';

import { socket } from 'lib/clients/socket';
import { appointmentKeys } from 'lib/queries/appointmentKeys';
import { COMMUNICATION_LOGS_QUERY_KEY } from 'lib/constants/queries/queryKeys';
import { invalidateAllAppointments, invalidateBulk } from 'lib/queries/appointmentInvalidation';

// lodash debounce keeps only the LAST invocation's args, so every coalesced event
// must accumulate here — scoped invalidation would otherwise silently drop the
// appointments broadcast earlier in the burst.
let bufferedRows = [];
let bufferHasUnscopedEvent = false;

const flushAppointmentUpdates = debounce(() => {
  const rows = bufferedRows;
  const needsFullInvalidation = bufferHasUnscopedEvent;
  bufferedRows = [];
  bufferHasUnscopedEvent = false;
  if (rows.length === 0) return;

  // An unscoped event (bulk actions, old-backend payloads) means "refresh
  // everything" — it must win even when scoped events coalesced into the
  // same flush window.
  if (needsFullInvalidation) {
    invalidateAllAppointments(queryClient);
    return;
  }

  invalidateBulk(queryClient, rows);

  // Parity with the old root nuke: pending and per-appointment invoice queries
  // also live under the appointments root and can change on an update echo.
  queryClient.invalidateQueries({ queryKey: appointmentKeys.pending() });
  for (const id of new Set(rows.map((row) => row.id).filter((id) => id != null))) {
    queryClient.invalidateQueries({ queryKey: appointmentKeys.invoices(id) });
  }
}, 300);

// Separate, slower debounce on purpose: the dashboard query is a heavy aggregate,
// so a burst of status changes must coalesce into a single refetch instead of
// riding the 300ms row flush. Prefix key + default refetchType — only mounted
// dashboards refetch.
const invalidateDashboard = debounce(() => {
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
}, 2000);

export default (data) => {
  const { appointment, patientId, oldStartsAt, invalidate, from } = data ?? {};

  // Communication logs refresh on every echo, including our own.
  queryClient.invalidateQueries({
    queryKey: appointment?.user_id
      ? [COMMUNICATION_LOGS_QUERY_KEY, appointment.user_id]
      : [COMMUNICATION_LOGS_QUERY_KEY],
    exact: false
  });

  // Skip only our OWN appointment refetch — this tab already refreshed via the
  // mutation. Checked before the debounce so a coalesced foreign echo isn't dropped.
  if (from && socket?.id && from === socket.id) return;

  // Patch per event, never debounced — the broadcast has to win the race against
  // the in-flight refetch. detailV2(id) throws on a null id, so guard.
  if (!invalidate && appointment?.id != null) {
    queryClient.setQueryData(appointmentKeys.detailV2(appointment.id), (oldData) => {
      if (!oldData) return;
      return {
        ...oldData,
        appointment: { ...(oldData?.appointment || {}), ...appointment }
      };
    });
  }

  const row = {
    id: appointment?.id,
    patientId: patientId ?? appointment?.user_id,
    startsAt: appointment?.starts_at
  };
  if (row.id == null && row.patientId == null) bufferHasUnscopedEvent = true;
  bufferedRows.push(row);
  // Date-only row: the calendar range the appointment moved out of. It adds no
  // id/patientId, so it can't distort invalidateBulk's no-patient fallback check.
  if (oldStartsAt) bufferedRows.push({ startsAt: oldStartsAt });

  flushAppointmentUpdates();
  invalidateDashboard();
};
